<template>
  <main class="container-fluid py-3">
    <b-button variant="outline-primary" class="mb-3" @click="loadData" :disabled="isLoading">
      {{ isLoading ? "loading..." : "Reload metadata" }}
    </b-button>
    <template v-if="metadata">
      <div class="mb-3">
        LBTC rate: <span class="font-monospace">{{ metadata.displayLbtcRate }}</span>
      </div>
      <div class="mb-3">
        <div>eBTC rate:</div>
        <div v-for="({ displayValue }, symbol) in metadata.rates" :key="symbol">
          <span class="font-monospace">{{ displayValue }}</span> {{ symbol }}
        </div>
      </div>
      <div class="mb-3">
        Total BTC value: <span class="font-monospace">{{ displayTotalBtcValue }}</span>
      </div>
      <table class="table table-hover mb-4">
        <thead>
        <tr>
          <th>Address</th>
          <th>Balances</th>
          <th>Offer</th>
          <th>Want</th>
          <th>Price</th>
          <th>Deadline</th>
          <th>Status</th>
        </tr>
        </thead>
        <tbody class="font-monospace">
        <tr v-for="item in metadata.addresses" :key="item.address" :class="{
          currentWallet: walletAddress === item.address,
          status0: item.atomicRequest.WBTC?.status === 0 || item.atomicRequest.LBTC?.status === 0
        }">
          <td>
            <div>
              <a :href="'https://etherscan.io/address/' + item.address" target="_blank">
                {{ item.address }}
              </a>
            </div>
            <div>
              <small class="text-secondary">ETH {{ item.displayEthBalance }}</small>
            </div>
            <div>
              <small class="text-secondary" :title="item.ebtcAllowance">eBTC approved {{ item.ebtcAllowance >= 10_00000000n }}</small>
            </div>
            <div v-if="walletAddress === item.address">
              <span class="text-primary">* connected wallet</span>
            </div>
          </td>
          <td>
            <div v-for="symbol in symbolsForBalance" :key="symbol" class="text-nowrap">
              {{ symbol }} {{ item.balance[symbol].displayValue }}
            </div>
          </td>
          <td>
            <template v-for="symbol in symbolsForAtomicRequest" :key="symbol">
              <div v-if="item.atomicRequest[symbol]" class="text-nowrap">
                {{ item.atomicRequest[symbol].displayOfferAmount }}
              </div>
            </template>
          </td>
          <td>
            <template v-for="symbol in symbolsForAtomicRequest" :key="symbol">
              <div v-if="item.atomicRequest[symbol]" class="text-nowrap">
                {{ item.atomicRequest[symbol].displayWantAmount }} {{ symbol }}
              </div>
            </template>
          </td>
          <td>
            <template v-for="symbol in symbolsForAtomicRequest" :key="symbol">
              <div v-if="item.atomicRequest[symbol]" class="text-nowrap">
                {{ item.atomicRequest[symbol].displayPrice }}
              </div>
            </template>
          </td>
          <td>
            <template v-for="symbol in symbolsForAtomicRequest" :key="symbol">
              <template v-if="item.atomicRequest[symbol]">
                <div class="text-nowrap">{{ item.atomicRequest[symbol].displayDeadline }}</div>
                <div class="text-nowrap">{{ item.atomicRequest[symbol].displayTimeLeft }}</div>
              </template>
            </template>
          </td>
          <td>
            <template v-for="symbol in symbolsForAtomicRequest" :key="symbol">
              <div v-if="item.atomicRequest[symbol]">
                {{ item.atomicRequest[symbol].status }}
              </div>
            </template>
          </td>
        </tr>
        </tbody>
      </table>

      <div class="mb-4">
        <BButton variant="outline-primary" @click="sendApprove">Send approve</BButton>
      </div>

      <section>
        <h4>Update Atomic Request</h4>
        <b-form @submit.prevent="submitForm">
          <b-form-group label="Amount in" class="mb-3">
            <b-form-input type="number" class="font-monospace" min="1e-8" max="5000" step="1e-8" required v-model="formInputAmountIn" />
          </b-form-group>
          <b-form-group label="Token out" class="mb-3">
            <b-form-radio-group :options="tokenOutOptions" v-model="formInputTokenOut" required @change="resetAtomicPrice" />
          </b-form-group>
          <b-form-group class="mb-3">
            <template #label>
              <span class="me-3">Atomic price</span>
              <span class="text-primary" style="cursor: pointer;" @click="resetAtomicPrice">Reset</span>
            </template>
            <b-form-input type="number" class="font-monospace" min="0.999" max="1.1" step="1e-8" required v-model="formInputAtomicPrice" />
          </b-form-group>
          <b-form-group label="Amount out" class="mb-3">
            <b-form-input class="font-monospace" disabled :value="displayAmountOut" />
          </b-form-group>
          <b-form-group class="mb-3">
            <template #label>
              <span class="me-3">Deadline</span>
              <span class="text-primary" style="cursor: pointer;" @click="resetDeadline">Reset</span>
            </template>
            <b-form-input type="datetime-local" v-model="formInputDeadline" required></b-form-input>
          </b-form-group>
          <b-button type="submit" variant="outline-primary">Submit</b-button>
        </b-form>
      </section>
    </template>
  </main>
</template>

<style scoped>
.currentWallet > td {
  background-color: aliceblue;
}
.status0 > td {
  background-color: cornsilk;
}
</style>

<script setup lang="ts">
  import { ref, reactive, computed, onMounted } from "vue";
  import * as ebtcService from "@/services/ebtcService";
  import {BButton, BForm, BFormGroup, BFormInput, BFormRadioGroup} from "bootstrap-vue-next";
  import BigNumber from "bignumber.js";
  import { walletAddress, chainId, browserProvider } from "@/services/onboard.ts";
  import {DateTime} from "luxon";
  import { ethers } from "ethers";

  const metadata = ref(null);
  const isLoading = ref(true);

  const symbolsForBalance = ["eBTC", "WBTC", "LBTC", "cbBTC"];
  const symbolsForAtomicRequest = ["WBTC", "LBTC", "cbBTC"];

  const formInputAmountIn = ref("");

  const tokenOutOptions = ["WBTC", "LBTC", "cbBTC"];
  const formInputTokenOut = ref("LBTC");

  const formInputAtomicPrice = ref("");
  const formInputDeadline = ref("");

  const displayTotalBtcValue = computed(() => {
    try {
      let ret = BigNumber(0);
      for (const addressObj of metadata.value.addresses) {
        for (const symbol in addressObj.balance) {
          const { displayValue } = addressObj.balance[symbol];
          ret = ret.plus(BigNumber(displayValue));
        }
      }
      return ret.decimalPlaces(8, BigNumber.ROUND_DOWN).toFixed();

    } catch (e) {}
    return "";
  });

  const displayAmountOut = computed(() => {
    const amountInBN = BigNumber(formInputAmountIn.value);
    const atomicPriceBN = BigNumber(formInputAtomicPrice.value);

    if (amountInBN.gt(0) && atomicPriceBN.gt(0)) {
      return amountInBN.multipliedBy(atomicPriceBN)
        .decimalPlaces(8, BigNumber.ROUND_DOWN)
        .toFixed();
    }
    return "";
  });

  onMounted(() => {
    document.title = "eBTC withdraw tool";
    loadData();
  });

  async function loadData() {
    try {
      isLoading.value = true;

      const m = await ebtcService.getMetadata([
        "0x7B946CB00cf9b652E469aa6ec68Afe419c745E30",
        "0x810Ae2176bCD09F360ae366164d8Fe3B076E3Ed0",
        "0x8C50ccA5F1B1bd76e0aB6e02e14475e362E0A292",
        "0x88902F6DdE52c4Ea3803348b159C2773a01E920a",
        "0x72c46989f2Ad352Cdc592FddF9c8544438AbC52c",
        "0x6af98902C057d344D33b40b6918938471a91Ecfe",
      ]);
      console.log(m);
      // m.addresses[0].wbtcAtomicRequest.status = 0;
      metadata.value = m;
      resetDeadline();
      resetAtomicPrice();

    } catch (error) {
      console.error(error);

    } finally {
      isLoading.value = false;
    }
  }

  async function sendApprove() {
    const txRequest = await ebtcService.ebtcContract.approve.populateTransaction(
      ebtcService.atomicQueueContract.target,
      ethers.MaxUint256
    );
    const signer = await browserProvider.value.getSigner();
    const tx = await signer.sendTransaction(txRequest);
  }

  function resetAtomicPrice() {
    const atomicPrice = metadata.value.rates[formInputTokenOut.value].value * 9999n / 10000n;
    formInputAtomicPrice.value = BigNumber(atomicPrice.toString()).shiftedBy(-8).toFixed();
  }

  function resetDeadline() {
    formInputDeadline.value = DateTime.now().plus({ days: 7 }).toFormat("yyyy-MM-dd'T'HH:mm");
  }

  async function submitForm() {
    const wantTokenContract = {
      WBTC: ebtcService.wbtcContract,
      LBTC: ebtcService.lbtcContract,
      cbBTC: ebtcService.cbbtcContract,
    }[formInputTokenOut.value];
    const deadline = DateTime.fromFormat(formInputDeadline.value, "yyyy-MM-dd'T'HH:mm").toUnixInteger();
    const atomicPrice = BigNumber(formInputAtomicPrice.value).shiftedBy(8).decimalPlaces(0).toFixed();
    const offerAmount = BigNumber(formInputAmountIn.value).shiftedBy(8).toFixed();

    const txRequest = await ebtcService.atomicQueueContract.updateAtomicRequest.populateTransaction(
      ebtcService.ebtcContract.target, // offer
      wantTokenContract.target, // want
      {
        deadline,
        atomicPrice,
        offerAmount,
        inSolve: false
      }
    );
    const signer = await browserProvider.value.getSigner();
    const tx = await signer.sendTransaction(txRequest);
  }

</script>

